import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Crown, X } from "lucide-react";

type Row = { user_id: string; email: string; display_name: string | null; since: string };

export default function AdminPayLater() {
  const [rows, setRows] = useState<Row[]>([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data, error } = await supabase.rpc("admin_list_pay_later_users" as any);
    if (error) { toast.error(error.message); return; }
    setRows((data as any) ?? []);
  };
  useEffect(() => { load(); }, []);

  const setFlag = async (email: string, allowed: boolean) => {
    setBusy(true);
    const { error } = await supabase.rpc("admin_set_pay_later_by_email" as any, { _email: email, _allowed: allowed });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success(allowed ? "Pay-later granted" : "Pay-later revoked");
    if (allowed) setEmail("");
    load();
  };

  return (
    <div className="pt-24 min-h-screen container mx-auto px-4 max-w-3xl pb-20">
      <h1 className="text-3xl font-display tracking-widest mb-2 flex items-center gap-2">
        <Crown className="w-6 h-6 text-primary" /> PAY-LATER CUSTOMERS
      </h1>
      <p className="text-sm text-muted-foreground font-body mb-8">
        These customers can book studio time without paying upfront. Atelier members are granted this automatically; you can also grant it manually here.
      </p>

      <div className="bg-panel border border-border p-6 mb-8">
        <Label htmlFor="email" className="text-xs font-mono uppercase tracking-widest">Grant by email</Label>
        <div className="flex gap-2 mt-2">
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="customer@example.com"
          />
          <Button onClick={() => setFlag(email, true)} disabled={!email || busy}>
            Grant
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">User must already have signed up.</p>
      </div>

      <div className="bg-panel border border-border">
        <div className="p-4 border-b border-border font-display tracking-widest text-sm">CURRENT MEMBERS ({rows.length})</div>
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">No customers have pay-later access yet.</p>
        ) : (
          <ul>
            {rows.map((r) => (
              <li key={r.user_id} className="flex items-center justify-between p-4 border-b border-border last:border-b-0">
                <div>
                  <div className="font-mono text-sm">{r.email}</div>
                  {r.display_name && <div className="text-xs text-muted-foreground">{r.display_name}</div>}
                  <div className="text-xs text-muted-foreground">Since {new Date(r.since).toLocaleDateString()}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setFlag(r.email, false)} disabled={busy}>
                  <X className="w-3 h-3 mr-1" /> Revoke
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}